import React from 'react';
import {
  Box,
  Container,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ForumIcon from '@mui/icons-material/Forum';
import HubIcon from '@mui/icons-material/Hub';
import TuneIcon from '@mui/icons-material/Tune';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import SettingsIcon from '@mui/icons-material/Settings';
import PsychologyIcon from '@mui/icons-material/Psychology';

import ChatView from './chat/ChatView';
import MCPServersView from './mcp/MCPServersView';
import AgentsView from './agents/AgentsView';
import AITuningView from './tuning/AITuningView';
import RagAdminView from './rag/RagAdminView';
import AIConfigView from './config/AIConfigView';
import { useNavigate, useParams } from 'react-router-dom';
import { ADMIN_URL_TOOLBOX_CONFIG } from '../PageStructure/admin-values';

type ActiveView = 'chat' | 'rag' | 'mcp' | 'agents' | 'tuning' | 'config';

const AIAdmin: React.FC = () => {
  const { tab1 } = useParams();
  const activeView: ActiveView = tab1 as ActiveView ?? 'chat';
  const navigate = useNavigate();


  const handleTabChange = (_event: React.SyntheticEvent, value: ActiveView) => {
    navigate(`${ADMIN_URL_TOOLBOX_CONFIG}/ai/${value}`);
  };

  return (
    <Container maxWidth={false} sx={{ py: 3 }}>
      <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
        <Box
          display="flex"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          gap={2}
          flexDirection={{ xs: 'column', md: 'row' }}
          mb={2}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <SmartToyIcon color="primary" />

            <Box>
              <Typography variant="h5" fontWeight={700}>
                AI Administration
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Manage chat, RAG blue/green mappings, MCP server registry, AI pipeline tuning, and shared AI config.
              </Typography>
            </Box>
          </Box>
        </Box>

        <Tabs
          value={activeView}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
        >
          <Tab
            icon={<ForumIcon fontSize="small" />}
            iconPosition="start"
            value="chat"
            label="Chat"
          />

          <Tab
            icon={<AccountTreeIcon fontSize="small" />}
            iconPosition="start"
            value="rag"
            label="RAG"
          />

          <Tab
            icon={<HubIcon fontSize="small" />}
            iconPosition="start"
            value="mcp"
            label="MCP"
          />

          <Tab
            icon={<PsychologyIcon fontSize="small" />}
            iconPosition="start"
            value="agents"
            label="Agents"
          />

          <Tab
            icon={<TuneIcon fontSize="small" />}
            iconPosition="start"
            value="tuning"
            label="Tuning"
          />

          <Tab
            icon={<SettingsIcon fontSize="small" />}
            iconPosition="start"
            value="config"
            label="Config"
          />
        </Tabs>

        <Box role="tabpanel" hidden={activeView !== 'chat'}>
          {activeView === 'chat' ? <ChatView /> : null}
        </Box>

        <Box role="tabpanel" hidden={activeView !== 'rag'}>
          {activeView === 'rag' ? <RagAdminView /> : null}
        </Box>

        <Box role="tabpanel" hidden={activeView !== 'mcp'}>
          {activeView === 'mcp' ? <MCPServersView /> : null}
        </Box>

        <Box role="tabpanel" hidden={activeView !== 'agents'}>
          {activeView === 'agents' ? <AgentsView /> : null}
        </Box>

        <Box role="tabpanel" hidden={activeView !== 'tuning'}>
          {activeView === 'tuning' ? <AITuningView /> : null}
        </Box>

        <Box role="tabpanel" hidden={activeView !== 'config'}>
          {activeView === 'config' ? <AIConfigView /> : null}
        </Box>
      </Paper>
    </Container>
  );
};

export default AIAdmin;